import { useState, useCallback } from "react";
import type {
  TransactionStatus,
  TransactionDirection,
} from "Infrastructure/Api/Api";
import type { FilterOption } from "Components/Shared/Filters/FilterOption";
import { FiltersTrigger } from "Components/Shared/Filters/FiltersTrigger";
import { TransactionFiltersDrawer } from "Components/Transactions/TransactionsSearchBar/TransactionFilters/TransactionFiltersDrawer";
import type {
  TransactionFilters,
  TimePeriod,
  TransactionFilterManager,
} from "Components/Transactions/hooks/useTransactionFilterManager";

type Props = {
  filterManager: TransactionFilterManager;
};

export const TransactionFilter = ({
  filterManager: {
    filters,
    availableFilters,
    applyFilters,
    resetFilters,
    activeFilterCount,
  },
}: Props) => {
  const [localFilters, setLocalFilters] = useState<TransactionFilters>(filters);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleTimePeriodChange = useCallback(
    (value: TimePeriod | undefined) => {
      setLocalFilters((prev) => ({
        ...prev,
        timePeriod: value,
        dateFrom: value === "custom" ? prev.dateFrom : undefined,
        dateTo: value === "custom" ? prev.dateTo : undefined,
      }));
    },
    [],
  );

  const handlePayPointChange = useCallback((option?: FilterOption) => {
    setLocalFilters((prev) => ({ ...prev, payPoint: option }));
  }, []);

  const handleEmployeeChange = useCallback((option?: FilterOption) => {
    setLocalFilters((prev) => ({ ...prev, employee: option }));
  }, []);

  const handleStatusChange = useCallback((value?: TransactionStatus) => {
    setLocalFilters((prev) => ({ ...prev, status: value }));
  }, []);

  const handleDirectionChange = useCallback((value?: TransactionDirection) => {
    setLocalFilters((prev) => ({ ...prev, direction: value }));
  }, []);

  const handleRefundOrCancellationChange = useCallback((value: boolean) => {
    setLocalFilters((prev) => ({ ...prev, isRefundOrCancellation: value }));
  }, []);

  const handleDateFromChange = useCallback((value?: Date) => {
    setLocalFilters((prev) => ({ ...prev, dateFrom: value }));
  }, []);

  const handleDateToChange = useCallback((value?: Date) => {
    setLocalFilters((prev) => ({ ...prev, dateTo: value }));
  }, []);

  const handleApply = useCallback(() => {
    applyFilters(localFilters);
    setIsDrawerOpen(false);
  }, [applyFilters, localFilters]);

  const handleReset = useCallback(() => {
    resetFilters();
    setIsDrawerOpen(false);
  }, [resetFilters]);

  return (
    <>
      <FiltersTrigger
        isOpen={isDrawerOpen}
        onClick={() => {
          setIsDrawerOpen(true);
          setLocalFilters(filters);
        }}
        activeFilterCount={activeFilterCount}
      />
      <TransactionFiltersDrawer
        isOpen={isDrawerOpen}
        availableFilters={availableFilters}
        localFilters={localFilters}
        onApply={handleApply}
        onReset={handleReset}
        onClose={() => setIsDrawerOpen(false)}
        onStatusChange={handleStatusChange}
        onDirectionChange={handleDirectionChange}
        onRefundOrCancellationChange={handleRefundOrCancellationChange}
        onDateToChange={handleDateToChange}
        onPayPointChange={handlePayPointChange}
        onEmployeeChange={handleEmployeeChange}
        onDateFromChange={handleDateFromChange}
        onTimePeriodChange={handleTimePeriodChange}
      />
    </>
  );
};
